import React, { useState, useEffect, useRef } from "react";
import { getUsersApi } from "../../../../api/api"; // Adjust path as needed

// Base URL where profile images are hosted. profileImage from the API is a
// relative path (e.g. "profile-images/xxxx.jpg"), so it must be prefixed
// with this base URL before being used as an <img src>.
const ASSET_BASE_URL = "https://s3.smartsuite.me/murasalone";

const EMPTY_FORM = {
  meetingType: "mursal",
  title: "",
  date: "",
  startTime: "",
  endTime: "",
  invite: "",
  description: "",
  reminder: "15",
  waitingRoomEnabled: true,
  recordingEnabled: false,
  allowGuests: false,
  includeJoinLink: false,
  transcriptEnabled: false,
  minutesEnabled: false,
};

function toDateStr(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toMinutes(time) {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

// Parses "YYYY-MM-DDTHH:MM:SS..." by splitting the string directly —
// never via `new Date(str)` — so the numbers shown/used are always
// exactly what came back from the API, with no timezone reinterpretation.
function parseAsIs(dateStr) {
  if (!dateStr) return null;
  const [datePart, timePart = "00:00"] = dateStr.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  return { year, month, day, hour, minute };
}

function toDateStrFromAsIs(dateStr) {
  const p = parseAsIs(dateStr);
  if (!p) return "";
  const y = p.year;
  const m = String(p.month).padStart(2, "0");
  const d = String(p.day).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatTimeForInput(dateStr) {
  if (!dateStr) return "";
  const [, timePart = ""] = dateStr.split("T");
  return timePart.slice(0, 5); // "HH:MM" exactly as received
}

// ==================== AVATAR ====================
// Renders the user's profile image (resolved against ASSET_BASE_URL).
// Falls back to a letter avatar if there's no profileImage, or if the
// image fails to load (onError), instead of leaking broken-image alt text.
function UserAvatar({ user, isSelected, size = 36 }) {
  const [imgError, setImgError] = useState(false);
  const imageUrl = user.profileImage
    ? `${ASSET_BASE_URL}/${user.profileImage}`
    : null;

  if (imageUrl && !imgError) {
    return (
      <img
        src={imageUrl}
        alt={user.fullName}
        onError={() => setImgError(true)}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: isSelected ? "#4F46E5" : "#6366f1",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "14px",
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {(user.fullName || user.email || "?")[0].toUpperCase()}
    </div>
  );
}

// ==================== COMPONENT ====================
function MeetingsDrawer({
  meetingType = "mursal",
  currentDate,
  onClose,
  onSubmit,
  editMode = false,
  meetingData = null,
}) {
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    meetingType,
    date: currentDate ? toDateStr(currentDate) : "",
  });
  const [formError, setFormError] = useState("");
  const [closing, setClosing] = useState(false);

  // User search/select states
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch all users on mount
  useEffect(() => {
    fetchAllUsers();
  }, []);

  // Filter users when searchQuery changes
  useEffect(() => {
    filterUsers(searchQuery);
  }, [searchQuery, allUsers, selectedUsers]);

  // Initialize form with meeting data when in edit mode
  useEffect(() => {
    if (editMode && meetingData) {
      setForm({
        meetingType: meetingData.type || meetingType,
        title: meetingData.title || "",
        date: meetingData.startTime
          ? toDateStrFromAsIs(meetingData.startTime)
          : toDateStr(currentDate),
        startTime: meetingData.startTime
          ? formatTimeForInput(meetingData.startTime)
          : "",
        endTime: meetingData.endTime
          ? formatTimeForInput(meetingData.endTime)
          : "",
        invite:
          meetingData.invite ||
          meetingData.participants?.map((p) => p.email).join(", ") ||
          "",
        description: meetingData.description || "",
        reminder: meetingData.reminder || "15",
        waitingRoomEnabled:
          meetingData.waitingRoomEnabled ?? meetingData.waitingRoom ?? true,
        recordingEnabled:
          meetingData.recordingEnabled ?? meetingData.recording ?? false,
        allowGuests: meetingData.allowGuests ?? false,
        includeJoinLink:
          meetingData.includeJoinLink ??
          meetingData.mursalLivekitEnabled ??
          false,
        transcriptEnabled: meetingData.transcriptEnabled ?? false,
        minutesEnabled: meetingData.minutesEnabled ?? false,
      });

      // Set selected users from existing participants
      if (meetingData.participants && meetingData.participants.length > 0) {
        setSelectedUsers(
          meetingData.participants.map((p) => ({
            email: p.email,
            displayName: p.displayName || p.email,
            id: p.userId || p.id,
            fullName: p.displayName || p.email,
          })),
        );
      }
    } else {
      setForm({
        ...EMPTY_FORM,
        meetingType,
        date: currentDate ? toDateStr(currentDate) : "",
      });
      setSelectedUsers([]);
    }
  }, [editMode, meetingData, meetingType, currentDate]);

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await getUsersApi(1, 100, "");
      if (response.data?.users) {
        setAllUsers(response.data.users);
        setDisplayedUsers(response.data.users); // Show all users initially
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const filterUsers = (query) => {
    if (!query || query.trim() === "") {
      // Show all users when search is empty
      setDisplayedUsers(allUsers);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = allUsers.filter((user) => {
      return (
        user.fullName?.toLowerCase().includes(lowerQuery) ||
        user.email?.toLowerCase().includes(lowerQuery) ||
        user.employeeNo?.toLowerCase().includes(lowerQuery) ||
        user.department?.toLowerCase().includes(lowerQuery) ||
        user.role?.toLowerCase().replace(/_/g, " ").includes(lowerQuery)
      );
    });

    setDisplayedUsers(filtered);
  };

  function handleClose() {
    setClosing(true);
    setTimeout(() => {
      onClose();
    }, 220);
  }

  function updateField(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function addUser(user) {
    setSelectedUsers((prev) => {
      if (prev.some((u) => u.email === user.email)) return prev;
      const updated = [...prev, user];
      updateField("invite", updated.map((u) => u.email).join(", "));
      return updated;
    });
  }

  function removeUser(email) {
    setSelectedUsers((prev) => {
      const updated = prev.filter((u) => u.email !== email);
      updateField("invite", updated.map((u) => u.email).join(", "));
      return updated;
    });
  }

  function handleInviteChange(e) {
    const value = e.target.value;
    updateField("invite", value);
    if (!value.trim()) {
      setSelectedUsers([]);
    }
  }

  function toggleUser(user) {
    const isSelected = selectedUsers.some((u) => u.email === user.email);
    if (isSelected) {
      removeUser(user.email);
    } else {
      addUser(user);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!form.title.trim()) return setFormError("Title is required.");
    if (!form.date) return setFormError("Date is required.");
    if (!form.startTime || !form.endTime)
      return setFormError("Start and end time are required.");
    if (toMinutes(form.endTime) <= toMinutes(form.startTime))
      return setFormError("End time must be after start time.");

    const startTimeStr = `${form.date}T${form.startTime}:00.000Z`;
    const endTimeStr = `${form.date}T${form.endTime}:00.000Z`;

    const payload = {
      // ✅ ADD THIS: Pass meetingType explicitly
      meetingType: form.meetingType,

      title: form.title,
      organizerEmail: meetingData?.organizerEmail || meetingData?.organizer,
      description: form.description,
      startTime: startTimeStr,
      endTime: endTimeStr,
      timezone:
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Dubai",
      attendees: selectedUsers.map((u) => ({ email: u.email })),
      location: null,
      recordingEnabled: form.recordingEnabled,
      transcriptEnabled: form.transcriptEnabled,
      minutesEnabled: form.minutesEnabled,
      waitingRoomEnabled: form.waitingRoomEnabled,

      // ✅ Also pass these for Exchange meetings
      includeJoinLink: form.includeJoinLink,
      meetingType: form.meetingType, // Already added above
    };

    console.log("📤 Submitting meeting payload:", payload);
    console.log("📤 Meeting type:", form.meetingType); // Debug log

    if (editMode && meetingData) {
      onSubmit({
        ...payload,
        id: meetingData.id,
        roomName: meetingData.livekitRoomName || meetingData.roomName,
      });
    } else {
      onSubmit(payload);
    }
  }

  const isMursal = form.meetingType === "mursal";

  return (
    <div
      className={`drawer-overlay ${closing ? "drawer-overlay--closing" : ""}`}
      onClick={handleClose}
    >
      <div
        className={`drawer ${closing ? "drawer--closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drawer__header">
          <div>
            <h2>{editMode ? "Edit Meeting" : "Create Meeting"}</h2>
            <p className="drawer__subtitle">
              {editMode
                ? `Update meeting details for "${meetingData?.title || form.title}"`
                : isMursal
                  ? "Creates a LiveKit room and meeting record"
                  : "Creates an Exchange / Outlook calendar event"}
            </p>
          </div>
          <button type="button" className="drawer__close" onClick={handleClose}>
            ×
          </button>
        </div>

        <form className="drawer__form" onSubmit={handleSubmit}>
          <div className="drawer__body">
            {/* Meeting type */}
            <div className="form-group">
              <label className="form-label">Meeting Type</label>
              <div className="type-toggle">
                <button
                  type="button"
                  className={`type-toggle__option ${
                    form.meetingType === "mursal" ? "active" : ""
                  }`}
                  onClick={() => updateField("meetingType", "mursal")}
                  disabled={editMode}
                >
                  <span className="type-toggle__dot type-toggle__dot--mursal" />
                  Mursal Meeting
                </button>
                <button
                  type="button"
                  className={`type-toggle__option ${
                    form.meetingType === "exchange" ? "active" : ""
                  }`}
                  onClick={() => updateField("meetingType", "exchange")}
                  disabled={editMode}
                >
                  <span className="type-toggle__dot type-toggle__dot--exchange" />
                  Exchange Meeting
                </button>
              </div>
            </div>

            {/* Title */}
            <div className="form-group">
              <label className="form-label" htmlFor="meeting-title">
                Title <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="meeting-title"
                type="text"
                className="form-input"
                placeholder="e.g. Weekly Standup, Project Review"
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
              />
            </div>

            {/* Date */}
            <div className="form-group">
              <label className="form-label" htmlFor="meeting-date">
                Date <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="meeting-date"
                type="date"
                className="form-input"
                value={form.date}
                onChange={(e) => updateField("date", e.target.value)}
              />
            </div>

            {/* Start / End */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="meeting-start">
                  Start Time <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  id="meeting-start"
                  type="time"
                  className="form-input"
                  value={form.startTime}
                  onChange={(e) => updateField("startTime", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="meeting-end">
                  End Time <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  id="meeting-end"
                  type="time"
                  className="form-input"
                  value={form.endTime}
                  onChange={(e) => updateField("endTime", e.target.value)}
                />
              </div>
            </div>

            {/* Invite Participants with User List Selection */}
            <div className="form-group" ref={dropdownRef}>
              <label className="form-label">
                Invite Participants
                <span
                  style={{
                    fontWeight: 400,
                    color: "#6b7280",
                    fontSize: "12px",
                    marginLeft: "8px",
                  }}
                >
                  ({selectedUsers.length} selected)
                </span>
              </label>

              {/* Selected Users Tags */}
              {selectedUsers.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    marginBottom: "10px",
                    padding: "10px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {selectedUsers.map((user) => (
                    <div
                      key={user.email}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "5px 10px",
                        background: "#4F46E5",
                        color: "white",
                        borderRadius: "16px",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      <span>
                        {user.fullName || user.displayName || user.email}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeUser(user.email)}
                        style={{
                          background: "rgba(255,255,255,0.2)",
                          border: "none",
                          cursor: "pointer",
                          padding: "0 4px",
                          color: "white",
                          fontSize: "14px",
                          lineHeight: "1",
                          borderRadius: "50%",
                          width: "18px",
                          height: "18px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search Input */}
              <div style={{ position: "relative", marginBottom: "10px" }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="🔍 Search by name, email, or employee number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: "12px" }}
                />
              </div>

              {/* User Selection List */}
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  maxHeight: "220px",
                  overflowY: "auto",
                  background: "white",
                }}
              >
                {loadingUsers ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#6b7280",
                    }}
                  >
                    <p style={{ fontSize: "13px", margin: 0 }}>
                      Loading users...
                    </p>
                  </div>
                ) : displayedUsers.length > 0 ? (
                  displayedUsers.map((user) => {
                    const isSelected = selectedUsers.some(
                      (u) => u.email === user.email,
                    );
                    return (
                      <div
                        key={user.id}
                        onClick={() => toggleUser(user)}
                        style={{
                          padding: "10px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          transition: "background 0.15s",
                          background: isSelected ? "#eef2ff" : "white",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected)
                            e.currentTarget.style.background = "#f9fafb";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected)
                            e.currentTarget.style.background = "white";
                        }}
                      >
                        {/* Checkbox */}
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "4px",
                            border: isSelected ? "none" : "2px solid #d1d5db",
                            background: isSelected ? "#4F46E5" : "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            transition: "all 0.15s",
                          }}
                        >
                          {isSelected && (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M10 3L4.5 8.5L2 6"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>

                        {/* Avatar (fixed: full URL + onError fallback) */}
                        <UserAvatar user={user} isSelected={isSelected} />

                        {/* User Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 500,
                              color: "#111827",
                              fontSize: "13px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {user.fullName || "Unknown"}
                          </div>
                          <div
                            style={{
                              color: "#6b7280",
                              fontSize: "12px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {user.email}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              marginTop: "2px",
                            }}
                          >
                            {user.employeeNo && (
                              <span
                                style={{ color: "#9ca3af", fontSize: "11px" }}
                              >
                                {user.employeeNo}
                              </span>
                            )}
                            {user.department && (
                              <span
                                style={{
                                  padding: "1px 6px",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                  background: "#f3f4f6",
                                  color: "#6b7280",
                                }}
                              >
                                {user.department}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Role Badge */}
                        <span
                          style={{
                            padding: "3px 8px",
                            borderRadius: "10px",
                            fontSize: "10px",
                            fontWeight: 600,
                            background: isSelected ? "#c7d2fe" : "#f3f4f6",
                            color: isSelected ? "#4338ca" : "#6b7280",
                            flexShrink: 0,
                          }}
                        >
                          {user.role?.replace(/_/g, " ")}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#6b7280",
                    }}
                  >
                    <p style={{ fontSize: "13px", margin: 0 }}>
                      {searchQuery
                        ? `No users found matching "${searchQuery}"`
                        : "No users available"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="form-group">
              <label className="form-label" htmlFor="meeting-desc">
                Description / Agenda
              </label>
              <textarea
                id="meeting-desc"
                className="form-input form-textarea"
                rows={3}
                placeholder="Discuss meeting agenda and objectives"
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
              />
            </div>

            {/* Type-specific options */}
            {isMursal ? (
              <div className="toggle-block">
                <ToggleRow
                  label="Recording"
                  checked={form.recordingEnabled}
                  onChange={(v) => updateField("recordingEnabled", v)}
                />
                <ToggleRow
                  label="Transcript (AI)"
                  checked={form.transcriptEnabled}
                  onChange={(v) => updateField("transcriptEnabled", v)}
                />
                <ToggleRow
                  label="Minutes of Meeting (AI)"
                  checked={form.minutesEnabled}
                  onChange={(v) => updateField("minutesEnabled", v)}
                />
                <ToggleRow
                  label="Waiting Room"
                  checked={form.waitingRoomEnabled}
                  onChange={(v) => updateField("waitingRoomEnabled", v)}
                />
                <ToggleRow
                  label="Allow Guests"
                  checked={form.allowGuests}
                  onChange={(v) => updateField("allowGuests", v)}
                />
                <p className="drawer__note">
                  {editMode
                    ? `LiveKit Room: ${meetingData?.livekitRoomName || meetingData?.roomName || "N/A"}`
                    : "A LiveKit room will be created automatically for this meeting."}
                </p>
              </div>
            ) : (
              <div className="toggle-block">
                <ToggleRow
                  label="Include Mursal Join Link"
                  checked={form.includeJoinLink}
                  onChange={(v) => updateField("includeJoinLink", v)}
                />
                <p className="drawer__note">
                  {editMode
                    ? "This event is synced with Exchange/Outlook calendar."
                    : `An Exchange/Outlook calendar event will be created${
                        form.includeJoinLink
                          ? " with a Mursal LiveKit join link."
                          : "."
                      }`}
                </p>
              </div>
            )}

            {formError && <div className="form-error">{formError}</div>}
          </div>

          <div className="drawer__footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editMode
                ? "Save Changes"
                : isMursal
                  ? "Create Mursal Meeting"
                  : "Create Exchange Meeting"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="toggle-row">
      <span className="toggle-row__label">{label}</span>
      <button
        type="button"
        className={`toggle-switch ${checked ? "toggle-switch--on" : ""}`}
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
      >
        <span className="toggle-switch__knob" />
      </button>
    </div>
  );
}

export default MeetingsDrawer;
